import { useState } from 'react';
import SiteHeader from "../../components/Header";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";


import { uploadToCloudinary } from '../../lib/cloudinary';

export default function CreateClientPage() {
  const [formData, setFormData] = useState({
    name: '',
    anotherName: '',
    email: '',
    phone: '',
    payment: '',
    paymentMethod: '',
    paymentStatus: 'Pending',
    paymentNote: '',
  });
  const [adhaarImages, setAdhaarImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultipleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setAdhaarImages(filesArray);
    }
  };

  const removeImage = (index) => {
    setAdhaarImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    if (adhaarImages.length === 0) {
      setMessage('Please upload at least one Adhaar image.');
      setLoading(false);
      return;
    }

    try {
      // Upload all images to Cloudinary
      const uploadPromises = adhaarImages.map(image => uploadToCloudinary(image));
      const imageUrls = await Promise.all(uploadPromises);

      // Submit to Internal API (Server-side Supabase)
      const response = await fetch('/api/clients', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          another_name: formData.anotherName,
          email: formData.email,
          phone: formData.phone,
          adhaar_images: imageUrls,
          payment: formData.payment || 0,
          payment_method: formData.paymentMethod,
          payment_status: formData.paymentStatus,
          payment_note: formData.paymentNote,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create client');
      }

      const data = await response.json();
      
      console.log('API Response:', data);

      setMessage('Client entry created successfully!');
      setFormData({ 
        name: '', 
        anotherName: '',
        email: '', 
        phone: '', 
        payment: '', 
        paymentMethod: '', 
        paymentStatus: 'Pending', 
        paymentNote: '' 
      });
      setAdhaarImages([]);
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
    } catch (err) {
      console.error(err);
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Create Client Entry" />
      <SiteHeader />
      <main>
        <div className="page-wrapper">
          <div className="page-header d-print-none">
            <div className="container-xl">
              <div className="row g-2 align-items-center">
                <div className="col">
                  <h2 className="page-title mb-1">Create Client Entry</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="page-body">
            <div className="container-xl">
              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <div className="card">
                    <div className="card-body">
                      {message && <div className={`alert ${message.includes('Error') ? 'alert-danger' : 'alert-success'}`}>{message}</div>}
                      
                      <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                          <label className="form-label required">Full Name</label>
                          <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} required />
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label">Another Name (Optional)</label>
                          <input type="text" className="form-control" name="anotherName" value={formData.anotherName} onChange={handleInputChange} placeholder="Nickname, preferred name, etc." />
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label required">Email</label>
                          <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} required />
                        </div>

                        <div className="mb-3">
                          <label className="form-label required">Phone Number</label>
                          <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} required />
                        </div>

                        <div className="hr-text">Documents</div>

                        <div className="mb-3">
                          <label className="form-label required">Adhaar Images</label>
                          <input 
                            type="file" 
                            className="form-control" 
                            accept="image/*" 
                            multiple 
                            onChange={handleMultipleFileChange} 
                            required 
                          />
                          <div className="form-text">You can select multiple images (front, back, etc.)</div>
                          
                          {adhaarImages.length > 0 && (
                            <div className="mt-3">
                              <div className="row g-2">
                                {adhaarImages.map((file, index) => (
                                  <div key={index} className="col-6 col-md-4">
                                    <div className="card">
                                      <div className="card-body p-2">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <small className="text-truncate">{file.name}</small>
                                          <button 
                                            type="button" 
                                            className="btn btn-sm btn-ghost-danger ms-2" 
                                            onClick={() => removeImage(index)}
                                            title="Remove"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="hr-text">Payment Details</div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Payment Amount (₹)</label>
                            <input type="number" className="form-control" name="payment" value={formData.payment} onChange={handleInputChange} min="0" />
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Payment Method</label>
                            <input type="text" className="form-control" name="paymentMethod" value={formData.paymentMethod} onChange={handleInputChange} list="payment-methods" placeholder="Cash, UPI, etc." />
                            <datalist id="payment-methods">
                              <option value="Cash" />
                              <option value="UPI" />
                              <option value="Bank Transfer" />
                              <option value="Cheque" />
                            </datalist>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">Payment Status</label>
                            <select className="form-select" name="paymentStatus" value={formData.paymentStatus} onChange={handleInputChange}>
                              <option value="Pending">Pending</option>
                              <option value="Completed">Completed</option>
                            </select>
                          </div>

                          <div className="col-md-12 mb-3">
                            <label className="form-label">Payment Note</label>
                            <textarea className="form-control" name="paymentNote" rows="2" value={formData.paymentNote} onChange={handleInputChange}></textarea>
                          </div>
                        </div>

                        <div className="form-footer">
                          <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating Entry...
                              </>
                            ) : (
                              'Create Entry'
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
